<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-4 sm:p-6">
            <div class="mx-auto rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6">
                <div class="mb-4 border-b border-gray-200 pb-3">
                    <h1 class="text-xl font-bold text-gray-800 sm:text-2xl">Prove di Consegna</h1>
                    <p class="mt-1 text-sm text-gray-500">Seleziona uno o più file Excel e un PDF con le scansioni.</p>
                </div>

                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <h2 class="mb-2 text-sm font-semibold text-gray-800">File Excel (uno o più)</h2>
                        <input
                            ref="excelFileInput"
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            multiple
                            class="hidden"
                            @change="onExcelFileChange"
                        />
                        <div
                            role="button"
                            tabindex="0"
                            class="flex min-h-40 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors"
                            :class="isExcelDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'"
                            @dragover.prevent="isExcelDragging = true"
                            @dragleave.prevent="onDragLeave($event, 'excel')"
                            @drop.prevent="onExcelDrop"
                            @click="openFilePicker('excel')"
                            @keydown.enter.prevent="openFilePicker('excel')"
                            @keydown.space.prevent="openFilePicker('excel')"
                        >
                            <v-icon name="bi-file-earmark-spreadsheet" scale="1.5" class="mb-2 text-green-600" />
                            <p class="text-sm font-medium text-gray-700">
                                {{ isExcelDragging ? 'Rilascia i file Excel' : 'Trascina qui uno o più file Excel oppure clicca per selezionarli' }}
                            </p>
                            <p class="mt-1 text-xs text-gray-500">Formato supportato: .xlsx</p>
                        </div>
                        <ul v-if="excelFiles.length" class="mt-2 space-y-1">
                            <li v-for="(file, index) in excelFiles" :key="`${file.name}-${file.size}-${file.lastModified}-${index}`" class="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
                                <span class="truncate text-sm font-medium text-gray-800">{{ file.name }}</span>
                                <button type="button" class="shrink-0 text-sm text-red-600 hover:text-red-800" :aria-label="`Rimuovi ${file.name}`" @click="removeExcelFile(index)">
                                    Rimuovi
                                </button>
                            </li>
                        </ul>
                        <p v-if="excelError" class="mt-2 text-sm text-red-700">{{ excelError }}</p>
                    </div>

                    <div>
                        <h2 class="mb-2 text-sm font-semibold text-gray-800">File PDF (uno)</h2>
                        <input
                            ref="pdfFileInput"
                            type="file"
                            accept=".pdf,application/pdf"
                            class="hidden"
                            @change="onPdfFileChange"
                        />
                        <div
                            role="button"
                            tabindex="0"
                            class="flex min-h-40 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors"
                            :class="isPdfDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'"
                            @dragover.prevent="isPdfDragging = true"
                            @dragleave.prevent="onDragLeave($event, 'pdf')"
                            @drop.prevent="onPdfDrop"
                            @click="openFilePicker('pdf')"
                            @keydown.enter.prevent="openFilePicker('pdf')"
                            @keydown.space.prevent="openFilePicker('pdf')"
                        >
                            <v-icon name="bi-file-earmark-pdf" scale="1.5" class="mb-2 text-red-500" />
                            <p class="text-sm font-medium text-gray-700">
                                {{ isPdfDragging ? 'Rilascia il PDF delle scansioni' : 'Trascina qui il PDF delle scansioni oppure clicca per selezionarlo' }}
                            </p>
                            <p class="mt-1 text-xs text-gray-500">Formato supportato: PDF</p>
                            <p v-if="pdfFile" class="mt-2 max-w-full truncate text-sm font-medium text-gray-800">{{ pdfFile.name }}</p>
                        </div>
                        <p v-if="pdfError" class="mt-2 text-sm text-red-700">{{ pdfError }}</p>
                        <div class="mb-3 flex flex-wrap items-center justify-end gap-3">
                        
                            <div class="flex flex-wrap gap-2 mt-3 ">
                                <PrimaryButton v-if="tableDocuments.length" type="button" :disabled="isSubmitting" @click="downloadProcessedExcel">
                                    Scarica Excel
                                </PrimaryButton>
                                <PrimaryButton type="button" :disabled="isSubmitting || !pdfFile" @click="submitPdf">
                                    <v-icon v-if="isSubmitting" name="hi-refresh" class="mr-2 animate-spin" />
                                    {{ isSubmitting ? 'Elaborazione...' : 'Elabora PDF' }}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section class="mx-auto mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6">
                <div class="mb-4 border-b border-gray-200 pb-3">
                    <h2 class="text-lg font-semibold text-gray-800">Confronto con i file Excel</h2>
                    <p class="mt-1 text-sm text-gray-500">
                        Al termine dell’elaborazione PDF, i dati estratti vengono confrontati con le righe dei file Excel caricati. Per le righe abbinate viene mostrato un valore integrato, dando precedenza ai dati della prova di consegna; ⚠ indica una differenza rispetto all’Excel.
                    </p>
                </div>
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <p v-if="isComparing" class="text-sm text-blue-700">Confronto dei dati in corso...</p>
                    <p v-else-if="comparisonError" class="text-sm text-red-700">{{ comparisonError }}</p>
                    <p v-else-if="comparisonRows.length" class="text-sm text-gray-600">{{ comparisonSummary }}</p>
                    <p v-else class="text-sm text-gray-500">Carica i file Excel e avvia l’elaborazione PDF per confrontare i DDT.</p>
                    <PrimaryButton
                        type="button"
                        :disabled="isComparing || !excelFiles.length || jobProgress?.status !== 'completed'"
                        @click="compareDocuments"
                    >
                        <v-icon v-if="isComparing" name="hi-refresh" class="mr-2 animate-spin" />
                        {{ isComparing ? 'Confronto...' : 'Confronta con Excel' }}
                    </PrimaryButton>
                </div>
                <div v-if="comparisonRows.length" class="mt-4 max-h-[50vh] overflow-auto">
                    <PageServerTable
                        :headers="comparisonTableHeaders"
                        :items="comparisonRows"
                        :items-length="comparisonRows.length"
                        :items-per-page="-1"
                        :reload-fn="noopReload"
                    />
                </div>
            </section>

            <div v-if="pdfPreviewUrl || tableDocuments.length" class="mx-auto mt-6 grid w-full grid-cols-1 gap-6 xl:grid-cols-2">
                <section class="min-h-[300px] min-w-0 rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6">
                    

                    <div v-if="isSubmitting && jobProgress" class="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <h3 class="font-semibold text-gray-800">Avanzamento elaborazione</h3>
                                <p class="mt-1 text-sm text-gray-600">{{ jobStatusLabel }}</p>
                            </div>
                            <span v-if="hasKnownProgress" class="text-sm font-semibold text-blue-800">{{ jobProgressPercentage }}%</span>
                            <v-icon v-else name="hi-refresh" class="animate-spin text-blue-600" />
                        </div>
                        <div
                            class="h-2.5 overflow-hidden rounded-full bg-blue-100"
                            role="progressbar"
                            :aria-valuenow="hasKnownProgress ? jobProgressPercentage : undefined"
                            :aria-valuemin="0"
                            :aria-valuemax="100"
                            :aria-label="jobStatusLabel"
                        >
                            <div
                                v-if="hasKnownProgress"
                                class="h-full rounded-full bg-blue-600 transition-all duration-500"
                                :style="{ width: `${jobProgressPercentage}%` }"
                            />
                            <div v-else class="h-full w-2/5 animate-progress-indeterminate rounded-full bg-blue-600" />
                        </div>
                        <p v-if="jobProgress.total_pages > 0" class="mt-1 text-sm text-gray-600">
                            Pagine elaborate: {{ jobProgress.processed_pages }} / {{ jobProgress.total_pages }}
                        </p>
                    </div>

                    <p v-if="processingError" class="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {{ processingError }}
                    </p>

                    <div v-if="tableDocuments.length" class="max-h-[70vh] overflow-auto">
                        <PageServerTable
                            :headers="documentTableHeaders"
                            :items="tableDocuments"
                            :items-length="tableDocuments.length"
                            :items-per-page="-1"
                            :reload-fn="noopReload"
                        />
                    </div>
                    <p v-else-if="jobProgress?.status === 'completed'" class="text-sm text-gray-500">
                        Nessuna prova di consegna trovata nel PDF.
                    </p>
                    <p v-else class="text-sm text-gray-500">
                        I dati estratti dal PDF appariranno qui al termine dell’elaborazione.
                    </p>
                </section>

                <section v-if="pdfPreviewUrl" class="min-w-0 rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6">
                    <h2 class="mb-3 text-lg font-semibold text-gray-800">Visualizzazione PDF</h2>
                    <iframe
                        :src="pdfPreviewUrl"
                        :title="pdfFile?.name || 'PDF caricato'"
                        class="h-[70vh] min-h-[400px] w-full rounded-md border border-gray-200"
                    />
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import axios from 'axios';
import ExcelJS from 'exceljs';

export default {
    components: {
        BreadCrumbs,
        PageServerTable,
        PrimaryButton,
    },
    data() {
        return {
            editableDocuments: [],
            comparisonError: '',
            comparisonRows: [],
            excelError: '',
            excelFiles: [],
            isComparing: false,
            isExcelDragging: false,
            isMounted: true,
            isPdfDragging: false,
            isSubmitting: false,
            jobProgress: null,
            pdfError: '',
            pdfFile: null,
            pdfPreviewUrl: null,
            processingError: '',
        };
    },
    beforeUnmount() {
        this.isMounted = false;
        this.revokePdfPreview();
    },
    computed: {
        jobStatusLabel() {
            return {
                queued: 'In attesa di elaborazione',
                processing: 'Elaborazione in corso',
                completed: 'Elaborazione completata',
                failed: 'Elaborazione non riuscita',
            }[this.jobProgress?.status] ?? 'Preparazione elaborazione';
        },
        hasKnownProgress() {
            return Number(this.jobProgress?.total_pages) > 0 || ['completed', 'failed'].includes(this.jobProgress?.status);
        },
        jobProgressPercentage() {
            if (this.jobProgress?.status === 'completed' && Number(this.jobProgress?.total_pages) <= 0) {
                return 100;
            }

            const totalPages = Number(this.jobProgress?.total_pages) || 0;
            const processedPages = Number(this.jobProgress?.processed_pages) || 0;

            return totalPages > 0 ? Math.min(100, Math.round((processedPages / totalPages) * 100)) : 0;
        },
        documentTableHeaders() {
            return [
                { title: 'Numero DDT', key: 'numero_ddt' },
                { title: 'Data DDT', key: 'data_ddt' },
                { title: 'Mittente', key: 'mittente' },
                { title: 'Destinatario', key: 'destinatario_ragione_sociale' },
                { title: 'Indirizzo', key: 'destinatario_indirizzo' },
                { title: 'Località', key: 'destinatario_localita' },
                { title: 'Provincia', key: 'destinatario_provincia' },
                { title: 'Colli', key: 'colli_sintesi' },
                { title: 'Peso stampato (kg)', key: 'peso_stampato' },
                { title: 'Peso manoscritto (kg)', key: 'peso_manoscritto' },
                { title: 'Misure stampate', key: 'misure_stampato' },
                { title: 'Misure manoscritte', key: 'misure_manoscritto' },
                { title: 'Data/ora trasporto stampata', key: 'data_ora_stampato' },
                { title: 'Data/ora consegna manoscritta', key: 'data_ora_manoscritto' },
                { title: 'Altre annotazioni manoscritte', key: 'altre_annotazioni_manoscritte' },
            ];
        },
        comparisonTableHeaders() {
            return [
                { title: 'Numero DDT', key: 'numero_ddt' },
                { title: 'Esito', key: 'stato_abbinamento' },
                { title: 'Data DDT', key: 'data_ddt' },
                { title: 'Mittente', key: 'mittente' },
                { title: 'Destinatario', key: 'destinatario' },
                { title: 'Indirizzo', key: 'indirizzo' },
                { title: 'Località', key: 'localita' },
                { title: 'Provincia', key: 'provincia' },
                { title: 'Colli', key: 'colli' },
                { title: 'Peso (kg)', key: 'peso_stampato' },
                { title: 'Peso manoscritto (kg)', key: 'peso_manoscritto' },
                { title: 'Misure stampate', key: 'misure_stampato' },
                { title: 'Misure manoscritte', key: 'misure_manoscritto' },
                { title: 'Data/ora consegna stampata', key: 'data_ora_stampato' },
                { title: 'Data/ora consegna manoscritta', key: 'data_ora_manoscritto' },
                { title: 'Annotazioni manoscritte', key: 'annotazioni_manoscritte' },
                { title: 'Conflitti', key: 'conflitti_sintesi' },
            ];
        },
        comparisonSummary() {
            const matched = this.comparisonRows.filter((row) => row.stato_abbinamento === 'abbinato').length;
            const unmatched = this.comparisonRows.filter((row) => row.stato_abbinamento === 'non_abbinato').length;
            const ambiguous = this.comparisonRows.filter((row) => row.stato_abbinamento === 'ambiguo').length;
            const conflicts = this.comparisonRows.reduce((total, row) => total + row.conflitti.length, 0);

            return `Abbinati: ${matched} · Non abbinati: ${unmatched} · Ambigui: ${ambiguous} · Conflitti: ${conflicts}`;
        },
        tableDocuments() {
            return this.editableDocuments;
        },
        navigation() {
            return [
                {
                    name: 'Dashboard',
                    href: route('dashboard'),
                },
                {
                    name: 'Riconoscimento Documenti AI',
                    href: route('modules.home', 'riconoscimento-documenti-ai'),
                },
                {
                    name: 'Prove di Consegna',
                    href: '#',
                },
            ];
        },
    },
    methods: {
        openFilePicker(type) {
            if (!this.isSubmitting) {
                this.$refs[type === 'excel' ? 'excelFileInput' : 'pdfFileInput']?.click();
            }
        },
        noopReload() {},
        async submitPdf() {
            if (this.isSubmitting || !this.pdfFile) {
                return;
            }

            this.processingError = '';
            this.comparisonError = '';
            this.comparisonRows = [];
            this.editableDocuments = [];
            this.jobProgress = {
                status: 'queued',
                processed_pages: 0,
                total_pages: 0,
                documenti: [],
            };
            this.isSubmitting = true;

            try {
                const formData = new FormData();
                formData.append('document', this.pdfFile);

                const response = await axios.post(route('modules.riconoscimento-documenti-ai.prove-di-consegna.chat'), formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                await this.pollJob(response.data.job_id);

                if (this.jobProgress?.status === 'completed' && this.excelFiles.length) {
                    await this.compareDocuments();
                }
            } catch (error) {
                if (this.isMounted) {
                    this.processingError = error.response?.data?.message ?? 'Errore durante l’elaborazione del PDF.';
                }
            } finally {
                if (this.isMounted) {
                    this.isSubmitting = false;
                }
            }
        },
        async pollJob(jobId) {
            while (this.isMounted) {
                const response = await axios.get(route('modules.riconoscimento-documenti-ai.prove-di-consegna.status', { jobId }));
                this.jobProgress = response.data;
                this.syncTableDocuments(response.data.documenti);

                if (response.data.status === 'completed') {
                    return;
                }

                if (response.data.status === 'failed') {
                    this.processingError = response.data.message || 'Impossibile elaborare il PDF.';
                    return;
                }

                await new Promise((resolve) => window.setTimeout(resolve, 2000));
            }
        },
        async compareDocuments() {
            if (this.isComparing || this.jobProgress?.status !== 'completed') {
                return;
            }

            if (!this.excelFiles.length) {
                this.comparisonError = 'Carica almeno un file Excel per avviare il confronto.';
                return;
            }

            this.isComparing = true;
            this.comparisonError = '';
            this.comparisonRows = [];

            try {
                const excelRows = await this.readExcelRows();
                const response = await axios.post(route('modules.riconoscimento-documenti-ai.prove-di-consegna.compare'), {
                    excel_rows: excelRows,
                    documenti: this.jobProgress.documenti ?? [],
                }, {
                    timeout: 180000,
                });

                if (!Array.isArray(response.data.righe)) {
                    throw new Error('La risposta del confronto non contiene righe valide.');
                }

                this.comparisonRows = response.data.righe.map((row) => {
                    const datiExcel = row.dati_excel ?? {};
                    const documento = row.dati_prove_di_consegna;
                    const conflitti = Array.isArray(row.conflitti) ? row.conflitti : [];
                    const colliConsegna = Array.isArray(documento?.colli) ? documento.colli.join(', ') : '';

                    return {
                        numero_ddt: this.integratedDisplayValue(datiExcel.numero_ddt, documento?.numero_ddt, conflitti, ['numero_ddt']),
                        stato_abbinamento: row.stato_abbinamento ?? 'ambiguo',
                        data_ddt: this.integratedDisplayValue(datiExcel.data_ddt, documento?.data_ddt, conflitti, ['data_ddt']),
                        mittente: this.integratedDisplayValue(datiExcel.mittente, documento?.mittente, conflitti, ['mittente']),
                        destinatario: this.integratedDisplayValue(datiExcel.destinatario_nome, documento?.destinatario?.ragione_sociale, conflitti, ['destinatario', 'destinatario_nome', 'ragione_sociale']),
                        indirizzo: this.integratedDisplayValue(datiExcel.destinatario_indirizzo, documento?.destinatario?.indirizzo, conflitti, ['indirizzo', 'destinatario_indirizzo']),
                        localita: this.integratedDisplayValue(datiExcel.destinatario_localita, documento?.destinatario?.localita, conflitti, ['localita', 'destinatario_localita']),
                        provincia: this.integratedDisplayValue(datiExcel.destinatario_provincia, documento?.destinatario?.provincia, conflitti, ['provincia', 'destinatario_provincia']),
                        colli: this.integratedDisplayValue(datiExcel.colli, colliConsegna, conflitti, ['colli']),
                        peso_stampato: this.integratedDisplayValue(datiExcel.chili, documento?.peso?.stampato, conflitti, ['peso', 'chili', 'peso_stampato']),
                        peso_manoscritto: documento?.peso?.manoscritto ?? '—',
                        misure_stampato: documento?.misure?.stampato || '—',
                        misure_manoscritte: documento?.misure?.manoscritto || '—',
                        data_ora_stampato: documento?.data_ora_consegna?.stampato ?? '—',
                        data_ora_manoscritta: documento?.data_ora_consegna?.manoscritto ?? '—',
                        annotazioni_manoscritte: documento?.altre_annotazioni_manoscritte || '—',
                        conflitti: conflitti,
                        conflitti_sintesi: conflitti.length
                            ? conflitti.map((conflitto) => `${conflitto.campo}: Excel "${conflitto.valore_excel}" / POD "${conflitto.valore_prove_di_consegna}"`).join(' · ')
                            : '—',
                    };
                });
            } catch (error) {
                if (this.isMounted) {
                    this.comparisonError = error.response?.data?.message ?? error.message ?? 'Errore durante il confronto dei dati.';
                }
            } finally {
                if (this.isMounted) {
                    this.isComparing = false;
                }
            }
        },
        integratedDisplayValue(excelValue, deliveryValue, conflicts, conflictFields) {
            const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
            const normalizeField = (value) => String(value ?? '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '');
            const conflictFieldNames = new Set(conflictFields.map(normalizeField));
            const hasConflict = conflicts.some((conflict) => conflictFieldNames.has(normalizeField(conflict.campo)));
            const value = hasValue(deliveryValue) ? deliveryValue : excelValue;

            if (!hasValue(value)) {
                return '—';
            }

            return `${hasConflict ? '⚠ ' : ''}${value}`;
        },
        async readExcelRows() {
            const fieldsByHeader = {
                numeroddt: 'numero_ddt',
                dataddt: 'data_ddt',
                mittente: 'mittente',
                destinatario: 'destinatario_nome',
                destinatarionome: 'destinatario_nome',
                destinatarioragionesociale: 'destinatario_nome',
                destinatarioindirizzo: 'destinatario_indirizzo',
                indirizzo: 'destinatario_indirizzo',
                destinatariolocalita: 'destinatario_localita',
                localita: 'destinatario_localita',
                destinatarioprovincia: 'destinatario_provincia',
                provincia: 'destinatario_provincia',
                colli: 'colli',
                chili: 'chili',
                peso: 'chili',
                pesokg: 'chili',
            };
            const excelRows = [];

            for (const file of this.excelFiles) {
                const workbook = new ExcelJS.Workbook();
                await workbook.xlsx.load(await file.arrayBuffer());

                workbook.worksheets.forEach((worksheet) => {
                    const headerRow = worksheet.getRow(1);
                    const columns = new Map();

                    headerRow.eachCell({ includeEmpty: false }, (cell, columnNumber) => {
                        const normalizedHeader = String(cell.text ?? '')
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '')
                            .toLowerCase()
                            .replace(/[^a-z0-9]/g, '');
                        const field = fieldsByHeader[normalizedHeader];

                        if (field) {
                            columns.set(columnNumber, field);
                        }
                    });

                    if (!columns.size) {
                        return;
                    }

                    worksheet.eachRow((row, rowNumber) => {
                        if (rowNumber === 1) {
                            return;
                        }

                        const excelRow = {
                            numero_ddt: '',
                            data_ddt: '',
                            mittente: '',
                            destinatario_nome: '',
                            destinatario_indirizzo: '',
                            destinatario_localita: '',
                            destinatario_provincia: '',
                            colli: '',
                            chili: '',
                        };

                        columns.forEach((field, columnNumber) => {
                            excelRow[field] = String(row.getCell(columnNumber).text ?? '').trim();
                        });

                        if (Object.values(excelRow).some((value) => value !== '')) {
                            excelRows.push(excelRow);
                        }
                    });
                });
            }

            if (!excelRows.length) {
                throw new Error('Non sono state trovate righe dati nei file Excel caricati.');
            }

            return excelRows;
        },
        syncTableDocuments(documents) {
            this.editableDocuments = Array.isArray(documents) ? documents.map((document) => {
                const destinatario = document.destinatario ?? {};
                const dataOraConsegna = document.data_ora_consegna ?? {};
                const colli = Array.isArray(document.colli) ? document.colli : null;

                const peso = document.peso ?? { stampato: null, manoscritto: null };
                const misure = document.misure ?? { stampato: '', manoscritto: '' };

                return {
                    numero_ddt: document.numero_ddt || '—',
                    data_ddt: document.data_ddt || '—',
                    mittente: document.mittente || '—',
                    destinatario_ragione_sociale: destinatario.ragione_sociale || '—',
                    destinatario_indirizzo: destinatario.indirizzo || '—',
                    destinatario_localita: destinatario.localita || '—',
                    destinatario_provincia: destinatario.provincia || '—',
                    colli_sintesi: colli?.join(', ') || '—',
                    peso_stampato: peso.stampato ?? '—',
                    peso_manoscritto: peso.manoscritto ?? '—',
                    misure_stampato: misure.stampato || '—',
                    misure_manoscritto: misure.manoscritto || '—',
                    data_ora_stampato: dataOraConsegna.stampato || '—',
                    data_ora_manoscritto: dataOraConsegna.manoscritto || '—',
                    altre_annotazioni_manoscritte: document.altre_annotazioni_manoscritte || '—',
                    colli,
                    peso,
                    misure,
                    data_ora_consegna: dataOraConsegna,
                };
            }) : [];
        },
        async downloadProcessedExcel() {
            if (!this.tableDocuments.length) {
                return;
            }

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Prove di consegna');
            const headers = [
                'Numero DDT',
                'Data DDT',
                'Mittente',
                'Destinatario',
                'Indirizzo',
                'Località',
                'Provincia',
                'Colli',
                'Peso stampato (kg)',
                'Peso manoscritto (kg)',
                'Misure stampate',
                'Misure manoscritte',
                'Data/ora consegna stampata',
                'Data/ora consegna manoscritta',
                'Altre annotazioni manoscritte',
            ];

            worksheet.addRow(headers);
            this.tableDocuments.forEach((document) => {
                worksheet.addRow([
                    document.numero_ddt,
                    document.data_ddt,
                    document.mittente ?? '',
                    document.destinatario?.ragione_sociale ?? '',
                    document.destinatario?.indirizzo ?? '',
                    document.destinatario?.localita ?? '',
                    document.destinatario?.provincia ?? '',
                    document.colli?.join(', ') ?? '',
                    document.peso?.stampato ?? '',
                    document.peso?.manoscritto ?? '',
                    document.misure?.stampato ?? '',
                    document.misure?.manoscritto ?? '',
                    document.data_ora_consegna?.stampato ?? '',
                    document.data_ora_consegna?.manoscritto ?? '',
                    document.altre_annotazioni_manoscritte,
                ]);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const baseName = this.getExportFileBaseName(this.pdfFile?.name, 'prove-di-consegna');

            this.downloadFile(blob, `${baseName}.xlsx`);
        },
        getExportFileBaseName(filename, fallback) {
            return (filename || fallback)
                .replace(/\.[^.]+$/, '')
                .replace(/[\\/:*?"<>|]/g, '_')
                .replace(/[. ]+$/, '') || fallback;
        },
        downloadFile(blob, filename) {
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = downloadUrl;
            link.download = filename;
            link.click();
            window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
        },
        onDragLeave(event, type) {
            if (!event.currentTarget.contains(event.relatedTarget)) {
                if (type === 'excel') {
                    this.isExcelDragging = false;
                } else {
                    this.isPdfDragging = false;
                }
            }
        },
        onExcelDrop(event) {
            this.isExcelDragging = false;
            this.addExcelFiles(event.dataTransfer.files);
        },
        onExcelFileChange(event) {
            this.addExcelFiles(event.target.files);
            event.target.value = '';
        },
        addExcelFiles(files) {
            if (this.isSubmitting || !files?.length) {
                return;
            }

            const selectedFiles = Array.from(files);
            const validFiles = selectedFiles.filter((file) => file.name.toLowerCase().endsWith('.xlsx'));
            const invalidFiles = selectedFiles.length - validFiles.length;

            this.excelFiles = [...this.excelFiles, ...validFiles];
            this.comparisonRows = [];
            this.comparisonError = '';
            this.excelError = invalidFiles === 1
                ? 'È stato ignorato un file non valido; seleziona file .xlsx.'
                : invalidFiles > 1
                    ? `Sono stati ignorati ${invalidFiles} file non validi; seleziona file .xlsx.`
                    : '';
        },
        removeExcelFile(index) {
            this.excelFiles.splice(index, 1);
            this.comparisonRows = [];
            this.comparisonError = '';
        },
        onPdfDrop(event) {
            this.isPdfDragging = false;
            this.setPdfFile(event.dataTransfer.files?.[0] ?? null);
        },
        onPdfFileChange(event) {
            const file = event.target.files?.[0] ?? null;
            event.target.value = '';
            this.setPdfFile(file);
        },
        setPdfFile(file) {
            if (!file || this.isSubmitting) {
                return;
            }

            this.pdfError = '';
            this.processingError = '';
            this.comparisonError = '';
            this.comparisonRows = [];
            this.jobProgress = null;
            this.editableDocuments = [];

            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                this.pdfFile = null;
                this.revokePdfPreview();
                this.pdfError = 'Seleziona un file PDF.';
                return;
            }

            this.revokePdfPreview();
            this.pdfFile = file;
            this.pdfPreviewUrl = URL.createObjectURL(file);
        },
        revokePdfPreview() {
            if (this.pdfPreviewUrl) {
                URL.revokeObjectURL(this.pdfPreviewUrl);
                this.pdfPreviewUrl = null;
            }
        },
    },
};
</script>

<style scoped>
@keyframes progress-indeterminate {
    from {
        transform: translateX(-100%);
    }
    to {
        transform: translateX(250%);
    }
}

.animate-progress-indeterminate {
    animation: progress-indeterminate 1.5s ease-in-out infinite;
}
</style>
